import { inject, Injectable } from '@angular/core';
import { DeepLinkParametersEnum, HostDataReaderService, QuoteToolStrategyService, TripDatesService } from '@lx/quote-tool/shared/services';
import { SchoolV4 } from '@lx/shared/data-access/school';
import { Country, Course } from '@lx/shared/data-access/types';
import { LoggerService } from '@lx/shared/logger';
import { SessionStorageService } from '@lx/shared/storage';
import { getDateFromString, getDateStringFromDate, isEmptyObj, normalizeString, QuoteToolPreselectedModes, valueIsNullOrUndefined } from '@lx/shared/utils';
import { sortArray } from '@lx/shared/utils-array';
import { combineLatest, Observable, of, tap } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { z, ZodSafeParseResult } from 'zod';
import { QuoteToolLiteState, SharedDataStore } from '../../../store';
import { CourseDetails } from '@lx/quote-tool/lite/shared/types';
import { QTLiteStudyAndStayDeepLinkParameters, QTLiteStudyAndStayRawDeepLinkParameters, RouteIds } from '../../../types';
import { CourseService } from '../../course.service';
import { StepDefinitionService } from '../../step-definition.service';
import { StudyAndStayDeepLinkParametersSchema } from '../../../schemas';
import { Flows, getQuoteToolStorageKey, SchoolLocation } from '@lx/quote-tool/lite/shared/types';

/**
 * Strategy service for handling Study and Stay deep links in Quote Tool Lite.
 * This service processes deep link parameters and prepares the state for the adult flow.
 */
@Injectable({
  providedIn: 'root',
})
export class StudyAndStayModeStrategyService implements QuoteToolStrategyService<RouteIds> {
  private readonly sharedDataStore = inject(SharedDataStore);
  private readonly courseService = inject(CourseService);
  private readonly logger = inject(LoggerService);
  private readonly sessionStorageService = inject(SessionStorageService);
  private readonly stepDefinitionService = inject(StepDefinitionService);
  private readonly hostDataReader = inject(HostDataReaderService);
  private readonly tripDatesService = inject(TripDatesService);
  private readonly storageKey = getQuoteToolStorageKey();

  getQuoteToolMode(): QuoteToolPreselectedModes {
    return QuoteToolPreselectedModes.studyAndStay;
  }

  getStepToNavigate(): Observable<RouteIds> | RouteIds {
    return of(RouteIds.course);
  }

  loadData$(): Observable<boolean> {
    const deepLinkParameters = this.getDeepLinkParameters();
    const parsedParameters = this.parseDeepLinkParameters(deepLinkParameters);

    if (!parsedParameters.success) {
      this.logger.error('Invalid deep link parameters', z.prettifyError(parsedParameters.error));
      return of(false);
    }

    const { schoolCode, courseCode, duration, startDate, deeplinkType } = parsedParameters.data;

    return combineLatest([this.sharedDataStore.schools$, this.sharedDataStore.countries$]).pipe(
      take(1),
      switchMap(([schools, countries]: [SchoolV4[], Country[]]) => {
        const selectedSchool = schools.find((s) => normalizeString(s.code) === normalizeString(schoolCode));
        const selectedCountry = selectedSchool ? countries.find((c) => normalizeString(c.code) === normalizeString(selectedSchool.country?.code)) : undefined;

        // If no school was provided or found, return empty
        if (!selectedSchool) {
          this.logger.error('School not found', schoolCode);
          return of(undefined);
        }

        // Fetch the adult courses for the school
        const courses$ = this.courseService.getAdultCourses(selectedSchool.code);
        return courses$.pipe(
          switchMap((courses: Course[]) => {
            // Find the course by code
            const matchedCourse = courses.find((c) => normalizeString(c.code) === normalizeString(courseCode));

            if (!matchedCourse) {
              this.logger.error('Course not found', courseCode);
              return of(undefined);
            }

            return of(matchedCourse);
          }),
          switchMap((course: Course) => {
            if (!course) {
              return of([undefined, undefined] as [Course, Date]);
            }

            const courseDates = course?.courseDates || [];
            const fixedLengthCourse = courseDates.some((courseDate) => courseDate?.length === duration);

            if (fixedLengthCourse) {
              const validatedStartDate = this.getValidatedStartDateForFixedCourse(courseDates, startDate);
              return combineLatest([of(course), of(validatedStartDate)]);
            }

            return combineLatest([of(course), this.getValidatedStartDateForFlexibleCourse(selectedSchool.code, startDate)]);
          }),
          map(([course, validatedStartDate]: [Course, Date]) =>
            this.prepareStudyAndStayForStore(selectedSchool, course, duration, validatedStartDate, selectedCountry, deeplinkType),
          ),
        );
      }),
      tap((studyAndStayData: Partial<QuoteToolLiteState>) => {
        if (!isEmptyObj(studyAndStayData)) {
          this.sessionStorageService.set(this.storageKey, { ...studyAndStayData });
        }
      }),
      map((selectedData: Partial<QuoteToolLiteState>) => !isEmptyObj(selectedData)),
      catchError((error) => {
        this.logger.error('Error during loadData$', error);
        return of(false);
      }),
    );
  }

  /**
   * Prepares the state data for the Study and Stay flow.
   */
  private prepareStudyAndStayForStore(
    school: SchoolV4,
    course: Course,
    duration: number,
    startDate: Date,
    country?: Country,
    deeplinkType?: string,
  ): Partial<QuoteToolLiteState & { currentStepId: string }> {
    if (!course) {
      return undefined;
    }

    const { code: schoolCode, name: schoolName } = school || {};
    const { code: countryCode, name: countryName } = country || {};
    const { type, code, durationMin, durationMax, courseDates, courseLanguage } = course || {};

    return {
      currentStepId: this.stepDefinitionService.getStepDefinition(RouteIds.course, Flows.Adult)?.id,
      activeFlow: Flows.Adult,
      selectedLanguage: courseLanguage,
      selectedSchoolLocation: this.getSelectedSchoolLocation(school, schoolCode, schoolName, countryCode, countryName),
      selectedCourse: this.getSelectedCourse(course, type, code, durationMin, durationMax, courseDates),
      selectedDuration: duration,
      selectedStartDate: getDateStringFromDate(startDate),
      deeplinkType,
    };
  }

  /**
   * Extracts the deep link parameters from the URL query parameters.
   * Note: startDate is kept as a raw string to allow Zod's z.coerce.date() to handle validation,
   * preventing uncaught exceptions during APP_INITIALIZER if the date format is invalid.
   */
  private getDeepLinkParameters(): QTLiteStudyAndStayRawDeepLinkParameters {
    const queryParams = this.hostDataReader.getQueryParams();
    const quoteToolModeQueryParam = this.hostDataReader.getQuoteToolMode();

    return {
      preselectedMode: quoteToolModeQueryParam ? (quoteToolModeQueryParam as QuoteToolPreselectedModes) : undefined,
      schoolCode: queryParams.get(DeepLinkParametersEnum.schoolCode) || undefined,
      courseCode: queryParams.get(DeepLinkParametersEnum.courseCode) || undefined,
      duration: Number(queryParams.get(DeepLinkParametersEnum.duration)) || undefined,
      startDate: queryParams.get(DeepLinkParametersEnum.startDate) || undefined,
      deeplinkType: queryParams.get(DeepLinkParametersEnum.deeplinkType) || undefined,
    };
  }

  /**
   * Validates the deep link parameters using the Zod schema.
   */
  private parseDeepLinkParameters(
    deepLinkParameters: QTLiteStudyAndStayRawDeepLinkParameters,
  ): ZodSafeParseResult<Partial<QTLiteStudyAndStayDeepLinkParameters>> {
    return StudyAndStayDeepLinkParametersSchema.safeParse(deepLinkParameters);
  }

  /**
   * Builds the course details object for the state.
   */
  private getSelectedCourse(
    course: Course,
    type: Course['type'],
    code: string,
    durationMin: number,
    durationMax: number,
    courseDates: Course['courseDates'],
  ): CourseDetails {
    if (!course) {
      return undefined;
    }

    return {
      type: type?.code,
      code,
      name: course.name,
      durationMin,
      durationMax,
      courseDates,
    };
  }

  /**
   * Builds the school location object for the state.
   */
  private getSelectedSchoolLocation(school: SchoolV4, schoolCode: string, schoolName: string, countryCode: string, countryName: string): SchoolLocation {
    if (!school) {
      return undefined;
    }

    return {
      schoolCode,
      schoolName,
      countryCode,
      countryName,
    };
  }

  /**
   * Validates the start date for fixed-length courses.
   * If the provided start date doesn't match any course date, falls back to the earliest available date.
   */
  private getValidatedStartDateForFixedCourse(courseDates: Course['courseDates'], startDate: Date | undefined): Date {
    // Ensure we have course dates to work with
    if (!courseDates?.length) {
      this.logger.error('No course dates available for start date validation');
      throw new Error('No course dates available for course date validation');
    }

    // Return early if the provided start date is valid
    if (!valueIsNullOrUndefined(startDate)) {
      const startDateString = getDateStringFromDate(startDate);
      if (courseDates.some((courseDate) => courseDate.from === startDateString)) {
        return startDate;
      }
    }

    // Fall back to earliest available date using sortArray utility
    this.logger.warn('Invalid start date for the selected course, taking the earliest available one');
    const sortedDates = sortArray(courseDates, [{ property: 'from' as const, direction: 'asc' }]);
    return getDateFromString(sortedDates[0].from);
  }

  /**
   * Validates the start date for flexible courses by checking against the school's calendar configuration.
   * If the start date is disabled, undefined, or outside the min/max date range, falls back to the minimum available date.
   */
  private getValidatedStartDateForFlexibleCourse(schoolCode: string, startDate: Date | undefined): Observable<Date> {
    return this.tripDatesService.getCalendarConfig(schoolCode).pipe(
      map((calendarConfig) => {
        // Short-circuit early if startDate is undefined
        if (valueIsNullOrUndefined(startDate)) {
          this.logger.warn('Start date is invalid (disabled, undefined, or outside min/max range), taking the minimum available date');
          return calendarConfig.minDate;
        }

        // Compute startDateString once to avoid repeated computation in the loop
        const startDateString = getDateStringFromDate(startDate);
        const startDateDisabled = calendarConfig.disabledDates.some((disabledDate) => getDateStringFromDate(disabledDate) === startDateString);
        const startDateBeforeMin = startDate < calendarConfig.minDate;
        const startDateAfterMax = startDate > calendarConfig.maxDate;

        if (startDateDisabled || startDateBeforeMin || startDateAfterMax) {
          this.logger.warn('Start date is invalid (disabled, undefined, or outside min/max range), taking the minimum available date');
          return calendarConfig.minDate;
        }

        return startDate;
      }),
    );
  }
}
